import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { directSupabase } from '../lib/supabaseClient';
import { phonesEquivalent } from '../elevate/phone';

// Define the customer type based on the API response
export interface Customer {
  id: number;
  name: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string | null;
  address: string;
  billing_address: string | null;
  organization_id: string;
  organization_name: string;
  created_at: string;
  updated_at: string;
  source?: 'local' | 'external'; // Make optional for backward compatibility
}

interface CustomerContextType {
  customers: Customer[];
  loading: boolean;
  error: string | null;
  refreshCustomers: () => Promise<void>;
  getCustomerById: (id: number) => Promise<Customer | undefined>;
  getCustomerByPhone: (phone: string) => Promise<Customer | undefined>;
  getCustomerByEmail: (email: string) => Promise<Customer | undefined>;
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

export function CustomerProvider({ children }: { children: React.ReactNode }) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching customers from combined endpoint...');
      
      // Get current Supabase session token
      const { data: { session } } = await directSupabase.auth.getSession();
      const accessToken = session?.access_token || localStorage.getItem('sb-access-token') || '';
      
      // Fetch customers from the enhanced Edge Function (both local and external)
      const response = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-customers', {
        headers: {
          'Authorization': accessToken ? `Bearer ${accessToken}` : '',
          'Content-Type': 'application/json'
        },
        mode: 'cors',
        credentials: 'omit'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCustomers(data.data || []);
        } else {
          console.warn('Failed to fetch customers:', data.error);
          setError(data.error || 'Failed to fetch customers');
        }
      } else {
        console.warn('Failed to fetch customers:', response.status);
        setError(`Failed to fetch customers: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      console.error('Error fetching customers:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions that use client-side filtering
  const getCustomerById = useCallback(async (id: number) => {
    try {
      setLoading(true);
      // Use client-side filtering from the already loaded customers
      const customer = customers.find(c => c.id === id);
      if (customer) {
        return customer;
      }
      
      // If not found in loaded customers, try fetching from the endpoint
      // Get current Supabase session token
      const { data: { session } } = await directSupabase.auth.getSession();
      const accessToken = session?.access_token || localStorage.getItem('sb-access-token') || '';
      
      const response = await fetch(`https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-customers/${id}`, {
        headers: {
          'Authorization': accessToken ? `Bearer ${accessToken}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch customer: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success && data.data) {
        // Update the customers list with the new customer
        setCustomers(prev => {
          const index = prev.findIndex(c => c.id === id);
          if (index >= 0) {
            const updated = [...prev];
            updated[index] = data.data;
            return updated;
          }
          return [...prev, data.data];
        });
        return data.data;
      }
      return undefined;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      console.error('Error fetching customer:', errorMessage);
      setError(errorMessage);
      return undefined;
    } finally {
      setLoading(false);
    }
  }, [customers]);

  const getCustomerByPhone = useCallback(async (phone: string) => {
    try {
      setLoading(true);
      // Use client-side filtering from the already loaded customers
      const customer = customers.find(c => phonesEquivalent(c.phone_number, phone));
      if (customer) {
        return customer;
      }
      
      // If not found in loaded customers, try fetching from the endpoint
      // Get current Supabase session token
      const { data: { session } } = await directSupabase.auth.getSession();
      const accessToken = session?.access_token || localStorage.getItem('sb-access-token') || '';
      
      const response = await fetch(`https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-customers/phone/${phone}`, {
        headers: {
          'Authorization': accessToken ? `Bearer ${accessToken}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customer');
      }

      const data = await response.json();
      if (data.success && data.data) {
        // Update the customers list with the new customer
        setCustomers(prev => {
          const index = prev.findIndex(c => phonesEquivalent(c.phone_number, phone));
          if (index >= 0) {
            const updated = [...prev];
            updated[index] = data.data;
            return updated;
          }
          return [...prev, data.data];
        });
        return data.data;
      }
      return undefined;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      console.error('Error fetching customer:', errorMessage);
      setError(errorMessage);
      return undefined;
    } finally {
      setLoading(false);
    }
  }, [customers]);

  const getCustomerByEmail = useCallback(async (email: string) => {
    try {
      setLoading(true);
      // Use client-side filtering from the already loaded customers
      const customer = customers.find(c => c.email === email);
      if (customer) {
        return customer;
      }
      
      // If not found in loaded customers, try fetching from the endpoint
      // Get current Supabase session token
      const { data: { session } } = await directSupabase.auth.getSession();
      const accessToken = session?.access_token || localStorage.getItem('sb-access-token') || '';
      
      const response = await fetch(`https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-customers/email/${email}`, {
        headers: {
          'Authorization': accessToken ? `Bearer ${accessToken}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customer');
      }

      const data = await response.json();
      if (data.success && data.data) {
        // Update the customers list with the new customer
        setCustomers(prev => {
          const index = prev.findIndex(c => c.email === email);
          if (index >= 0) {
            const updated = [...prev];
            updated[index] = data.data;
            return updated;
          }
          return [...prev, data.data];
        });
        return data.data;
      }
      return undefined;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      console.error('Error fetching customer:', errorMessage);
      setError(errorMessage);
      return undefined;
    } finally {
      setLoading(false);
    }
  }, [customers]);

  const value = {
    customers,
    loading,
    error,
    refreshCustomers: fetchCustomers,
    getCustomerById,
    getCustomerByPhone,
    getCustomerByEmail,
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomerContext() {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error('useCustomerContext must be used within a CustomerProvider');
  }
  return context;
} 